import { BarChart3 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  FIT,
  MAX_ZOOM_SLOT,
  slotOfZoom,
  zoomAtSlot,
  zoomLabel,
  type PayoffZoom,
} from '@/components/payoff/payoffZoom'

export function Stat({
  label,
  value,
  tone,
  hint,
  assumption,
  testId,
}: {
  label: string
  value: string
  tone?: string
  hint?: string
  /** Shown when this figure depends on an assumption the reader has moved. */
  assumption?: string | undefined
  testId: string
}) {
  return (
    <div className="px-4 py-2" title={hint}>
      <div className="flex items-center gap-1.5 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {label}
        {/*
          A figure computed at an assumption the reader has changed says
          so, so the strip cannot be read as five facts when two of them
          are answers to a question the slider asked.
        */}
        {assumption && (
          <span className="num rounded bg-primary/15 px-1 text-[9px] font-bold text-primary">
            {assumption}
          </span>
        )}
      </div>
      <div className={cn('num mt-0.5 truncate text-[15px] font-semibold', tone)} data-testid={testId}>
        {value}
      </div>
    </div>
  )
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  testId,
}: {
  value: T
  onChange: (value: T) => void
  options: readonly { value: T; label: string; icon?: typeof BarChart3 }[]
  testId: string
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-secondary p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`${testId}-${o.value.toLowerCase()}`}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'flex h-7 items-center gap-1.5 rounded-md px-3 text-[11px] font-semibold transition-colors',
            value === o.value
              ? 'bg-card text-foreground shadow-[0_1px_2px_rgb(15_23_42/8%)]'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.icon && <o.icon size={12} />}
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Slider({
  label,
  value,
  display,
  min,
  max,
  step,
  onChange,
  hint,
  testId,
}: {
  label: string
  hint?: string
  value: number
  display: string
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  testId: string
}) {
  return (
    <label className="flex items-center gap-2" title={hint}>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`${testId}-slider`}
        className="tr-slider w-24"
        aria-label={`${label} ${display}`}
      />
      {/* Fixed width, so the row does not jump as the number changes. */}
      <span className="num w-[52px] text-[11px] font-semibold" data-testid={`${testId}-value`}>
        {display}
      </span>
    </label>
  )
}

/**
 * The x-axis zoom — ROADMAP 9.46.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO AFFORDANCES OVER ONE VALUE
 *
 * `Fit` and the slider are not two settings. They are two ways to write the
 * same `PayoffZoom`, which is why this is one component holding one value
 * rather than a boolean beside a number — a pair that can disagree, and that
 * someone eventually reads in the wrong order.
 *
 * THE LABEL ALWAYS SAYS WHAT IS ACTUALLY DRAWN. In fit mode it names the
 * span fit resolved to ("Fit ±6.3%"), because "Fit" alone tells the reader
 * the mode and not the scale, and the scale is what they are judging the
 * curve against.
 *
 * TOUCHING THE SLIDER LEAVES FIT, and starts from the stop nearest what fit
 * was showing — see `slotOfZoom`. A control that jumped to one end the
 * moment you touched it would make fit a mode you avoid entering.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ZoomControl({
  zoom,
  resolvedPct,
  onChange,
  testId,
}: {
  zoom: PayoffZoom
  /** What the window currently spans, as a half-percentage. Null with no spot. */
  resolvedPct: number | null
  onChange: (zoom: PayoffZoom) => void
  testId: string
}) {
  const slot = slotOfZoom(zoom, resolvedPct)
  const fitted = zoom.kind === 'AUTO'
  return (
    <label
      className="flex items-center gap-2"
      title="Changes the displayed spot window, not the held trade. Fit covers every strike in the position."
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Range
      </span>
      <button
        type="button"
        onClick={() => onChange(fitted ? zoomAtSlot(slot) : FIT)}
        aria-pressed={fitted}
        data-testid={`${testId}-fit`}
        className={cn(
          'h-7 rounded-md border px-2 text-[10px] font-semibold uppercase tracking-wider transition-colors',
          fitted
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
      >
        Fit
      </button>
      <input
        type="range"
        min={0}
        max={MAX_ZOOM_SLOT}
        step={1}
        value={slot}
        onChange={(e) => onChange(zoomAtSlot(Number(e.target.value)))}
        data-testid={`${testId}-slider`}
        className="tr-slider w-24"
        aria-label={`Range ${zoomLabel(zoom, resolvedPct)}`}
      />
      {/*
        Wide enough for the longest string this can produce ("Fit ±12.34%"),
        so the footer does not reflow as the reader drags.
      */}
      <span className="num w-[76px] text-[11px] font-semibold" data-testid={`${testId}-value`}>
        {zoomLabel(zoom, resolvedPct)}
      </span>
    </label>
  )
}
